import { ERROR_CODES } from "@dusk/connect";
import { installDemoWallets } from "portal-kit/mock-wallet.js";
import {
  Badge,
  Button,
  WalletOption,
  ErrorBanner,
  ActionBar,
  Tabs,
  TextField,
} from "frontend-kit/components/index.js";
import { initChainApp, getChainApp, getBlockHeight } from "./chain-app.js";
import {
  connectedMoonlightAccount,
  isContractOwner,
  isPrivilegedCreator,
  walletHasCreateRight,
} from "./chain-config.js";
import { loadBackendConfig, saveBackendConfig } from "./gateway-api.js";
import { isParticipantReady, loadParticipant } from "./participant-store.js";
import { clearIdentitySession } from "./session-identity.js";
import { loadTradingKey } from "./trading-key-store.js";
import { hexToBytes } from "./trader-crypto.js";
import { renderBrowsePage } from "./browse-page.js";
import { renderMarketDetail } from "./market-detail.js";
import { renderPortfolioPage } from "./portfolio-page.js";
import { renderLeaderboardPage } from "./leaderboard-page.js";
import { renderAchievementsPage } from "./achievements-page.js";
import { renderSettingsPage } from "./settings-page.js";
import { renderCreatePage } from "./create-page.js";
import { parseCommitmentRoute } from "./commitment-route.js";
import { renderTraderPage } from "./trader-page.js";
import { renderAgentsPage } from "./agents-page.js";
import { renderAgentPage } from "./agent-page.js";
import { ABOUT_SECURITY_CTA } from "./guide-copy.js";
import { parseSettingsSection } from "./settings-sections.js";
import { ABOUT_HERO, ABOUT_SECTIONS, HOW_IT_WORKS, BRAND } from "./privacy-copy.js";
import { LEGAL_SECTIONS, privacyPolicyLink } from "./legal-copy.js";
import { initGlobalTxBanner } from "./tx-status.js";
import { icon, iconNavButton } from "./icons.js";
import { applyCbMode, loadCbMode, saveCbMode } from "./theme-prefs.js";
import { renderDepositPanel } from "./deposit-panel.js";
import { renderFeedbackPanel } from "./feedback-panel.js";
import { duskAmountNode } from "./dusk-amount.js";

document.title = BRAND.title;

const params = new URLSearchParams(location.search);
const demoWallets = params.get("demoWallets") === "1";
const debugMode = params.get("debug") === "1";

const logEl = document.getElementById("log");
const debugLog = document.getElementById("debugLog");
if (debugMode && debugLog) debugLog.hidden = false;

initGlobalTxBanner(document.getElementById("globalTxBanner"));

function log(line) {
  if (!logEl) return;
  const stamp = new Date().toLocaleTimeString();
  const participant = loadParticipant();
  const prefix = participant?.participant_id
    ? `[${stamp}] [p:${participant.participant_id.slice(0, 8)}] `
    : `[${stamp}] `;
  logEl.textContent = `${prefix}${line}\n${logEl.textContent}`;
}

if (demoWallets) {
  installDemoWallets();
  log("demo wallets announced (?demoWallets=1)");
}

const dusk = initChainApp();
const wallet = dusk.wallet;

let uiPhase = "checking";
let lastError = null;
let route = parseRoute();
let popoverOpen = false;
let popoverBalanceLux = null;

function parseRoute() {
  const h = location.hash.replace(/^#\/?/, "") || "events";
  const parts = h.split("/");
  const commitmentRoute = (() => {
    try {
      return parseCommitmentRoute(parts);
    } catch {
      return { name: "invalid-route" };
    }
  })();
  if (commitmentRoute) return commitmentRoute;
  // Legacy `#/markets` aliases → events
  const browse = parts[0] === "markets" ? "events" : parts[0];
  if (browse === "events" && parts[1]) {
    return { name: "detail", id: Number(parts[1]) };
  }
  if (browse === "events") return { name: "events" };
  if (parts[0] === "portfolio") return { name: "portfolio" };
  if (parts[0] === "leaderboard") return { name: "leaderboard" };
  if (parts[0] === "achievements") return { name: "achievements" };
  if (parts[0] === "about") return { name: "about" };
  if (parts[0] === "settings") {
    return {
      name: "settings",
      section: parseSettingsSection(parts.join("/")),
    };
  }
  if (parts[0] === "create") return { name: "create" };
  return { name: "events" };
}

window.addEventListener("hashchange", () => {
  route = parseRoute();
  closeAllPopovers();
  renderApp();
});

function computePhase(state) {
  if (!state.installed) return "not-installed";
  if (state.authorized && state.profiles.length) return "connected";
  if (state.availableProviders.length > 1 && !state.providerId) return "picking";
  return "idle";
}

const walletCardBody = document.getElementById("walletCardBody");
const appTabs = document.getElementById("appTabs");
const appBody = document.getElementById("appBody");
const walletIconBtn = document.getElementById("walletIconBtn");
const walletPopover = document.getElementById("walletPopover");
const walletPopoverClose = document.getElementById("walletPopoverClose");
const walletStatusDot = document.getElementById("walletStatusDot");
const collateralChip = document.getElementById("collateralChip");
const fundsAnchor = document.getElementById("fundsAnchor");
const fundsPopover = document.getElementById("fundsPopover");
const fundsPopoverBody = document.getElementById("fundsPopoverBody");
const fundsPopoverClose = document.getElementById("fundsPopoverClose");
const walletIconSlot = document.getElementById("walletIconSlot");
const feedbackIconBtn = document.getElementById("feedbackIconBtn");
const feedbackPopover = document.getElementById("feedbackPopover");
const feedbackPopoverBody = document.getElementById("feedbackPopoverBody");
const feedbackPopoverClose = document.getElementById("feedbackPopoverClose");
const feedbackIconSlot = document.getElementById("feedbackIconSlot");
const feedbackAnchor = document.getElementById("feedbackAnchor");
const statusPillHeight = document.getElementById("statusPillHeight");
const statusPillIcon = document.getElementById("statusPillIcon");
const cbToggleBtn = document.getElementById("cbToggleBtn");

applyCbMode(document.documentElement, loadCbMode());
if (statusPillIcon) {
  statusPillIcon.replaceChildren(icon("block", { size: 10 }));
}
if (cbToggleBtn) {
  cbToggleBtn.append(icon("contrast", { size: 16 }));
  const syncCbBtn = () => {
    const on = loadCbMode();
    cbToggleBtn.classList.toggle("on", on);
    cbToggleBtn.setAttribute("aria-pressed", on ? "true" : "false");
  };
  syncCbBtn();
  cbToggleBtn.addEventListener("click", () => {
    const next = !loadCbMode();
    saveCbMode(next);
    applyCbMode(document.documentElement, next);
    syncCbBtn();
  });
}

if (walletIconSlot) {
  walletIconSlot.replaceChildren(icon("wallet", { size: 22 }));
}
if (feedbackIconSlot) {
  feedbackIconSlot.replaceChildren(icon("feedback", { size: 20 }));
}

let fundsOpen = false;
let feedbackOpen = false;
let lastChipLux = null;

function paragraph(text) {
  const p = document.createElement("p");
  p.className = "hint";
  p.textContent = text;
  return p;
}

function paintCollateralChip(lux) {
  if (!collateralChip) return;
  if (lux == null) {
    if (fundsAnchor) fundsAnchor.hidden = true;
    collateralChip.hidden = true;
    popoverBalanceLux = null;
    lastChipLux = null;
    return;
  }
  lastChipLux = BigInt(lux);
  if (fundsAnchor) fundsAnchor.hidden = false;
  collateralChip.hidden = false;
  collateralChip.replaceChildren();
  collateralChip.append(icon("deposit", { size: 14, className: "collateral-chip-icon" }));
  const label = document.createElement("span");
  label.className = "collateral-chip-label";
  label.textContent = "Free";
  const strong = document.createElement("strong");
  strong.append(duskAmountNode(lastChipLux));
  collateralChip.append(label, strong);
  popoverBalanceLux = lastChipLux;
}

function setWalletPopoverOpen(open) {
  popoverOpen = open;
  if (walletPopover) walletPopover.hidden = !open;
  if (walletIconBtn) walletIconBtn.setAttribute("aria-expanded", open ? "true" : "false");
}

function setFundsPopoverOpen(open) {
  fundsOpen = open;
  if (fundsPopover) fundsPopover.hidden = !open;
  if (collateralChip) collateralChip.setAttribute("aria-expanded", open ? "true" : "false");
}

function setFeedbackPopoverOpen(open) {
  feedbackOpen = open;
  if (feedbackPopover) feedbackPopover.hidden = !open;
  if (feedbackIconBtn) feedbackIconBtn.setAttribute("aria-expanded", open ? "true" : "false");
}

function closePopover() {
  setWalletPopoverOpen(false);
}

function closeFundsPopover() {
  setFundsPopoverOpen(false);
}

function closeFeedbackPopover() {
  setFeedbackPopoverOpen(false);
}

function closeAllPopovers() {
  closePopover();
  closeFundsPopover();
  closeFeedbackPopover();
}

function openPopover() {
  closeFundsPopover();
  closeFeedbackPopover();
  setWalletPopoverOpen(true);
  renderWalletCard(wallet.state);
}

function openFeedbackPopover() {
  // Operator-only for now (same gate as Create). Widen when market creation
  // is opened beyond the contract-owner wallet - see setFeedbackVisible.
  if (!isContractOwner(wallet)) {
    closeFeedbackPopover();
    setFeedbackVisible(false);
    return;
  }
  closePopover();
  closeFundsPopover();
  if (feedbackOpen) {
    closeFeedbackPopover();
    return;
  }
  if (feedbackPopoverBody) {
    feedbackPopoverBody.replaceChildren(
      renderFeedbackPanel({
        route,
        onDone: () => {
          // Leave open briefly so the user sees the thanks message.
          setTimeout(() => closeFeedbackPopover(), 1400);
        },
      })
    );
  }
  setFeedbackPopoverOpen(true);
}

/**
 * Feedback chrome is operator-only today: same connected-wallet check as
 * Create (`isContractOwner`). When market creation opens to more wallets,
 * drop this gate (and the matching check in `openFeedbackPopover`) so any
 * signed-in - or even anonymous - user can send feedback again.
 */
function setFeedbackVisible(visible) {
  if (feedbackAnchor) feedbackAnchor.hidden = !visible;
  if (!visible) closeFeedbackPopover();
}

function openHeaderFunds(initialTab = "deposit") {
  closePopover();
  closeFeedbackPopover();
  if (!isParticipantReady()) {
    go("#/settings");
    return;
  }
  if (fundsOpen) {
    closeFundsPopover();
    return;
  }
  if (fundsPopoverBody) {
    fundsPopoverBody.replaceChildren(
      renderDepositPanel({
        log,
        initialTab,
        initialBalance: lastChipLux,
        onDone: ({ balanceLux }) => {
          setCollateralChipLux(balanceLux);
          if (route.name === "portfolio") renderApp();
        },
      })
    );
  }
  setFundsPopoverOpen(true);
}

function isConnectBusy() {
  return uiPhase === "connecting" || computePhase(wallet.state) === "picking";
}

walletIconBtn?.addEventListener("click", (e) => {
  e.stopPropagation();
  if (popoverOpen) closePopover();
  else openPopover();
});

walletPopoverClose?.addEventListener("click", (e) => {
  e.stopPropagation();
  closePopover();
});

walletPopover?.addEventListener("click", (e) => e.stopPropagation());

collateralChip?.addEventListener("click", (e) => {
  e.stopPropagation();
  openHeaderFunds("deposit");
});

fundsPopoverClose?.addEventListener("click", (e) => {
  e.stopPropagation();
  closeFundsPopover();
});

fundsPopover?.addEventListener("click", (e) => e.stopPropagation());

feedbackIconBtn?.addEventListener("click", (e) => {
  e.stopPropagation();
  openFeedbackPopover();
});

feedbackPopoverClose?.addEventListener("click", (e) => {
  e.stopPropagation();
  closeFeedbackPopover();
});

feedbackPopover?.addEventListener("click", (e) => e.stopPropagation());

// Use composedPath - after Connect re-renders, e.target may be detached and
// popover.contains(target) would wrongly look like an outside click.
document.addEventListener("click", (e) => {
  const path = typeof e.composedPath === "function" ? e.composedPath() : [];
  if (popoverOpen && !isConnectBusy()) {
    if (!path.includes(walletPopover) && !path.includes(walletIconBtn)) {
      closePopover();
    }
  }
  if (fundsOpen) {
    if (!path.includes(fundsPopover) && !path.includes(collateralChip) && !path.includes(fundsAnchor)) {
      closeFundsPopover();
    }
  }
  if (feedbackOpen) {
    if (!path.includes(feedbackPopover) && !path.includes(feedbackIconBtn)) {
      closeFeedbackPopover();
    }
  }
});

document.addEventListener("keydown", (e) => {
  if (e.key !== "Escape") return;
  if (feedbackOpen) closeFeedbackPopover();
  else if (fundsOpen) closeFundsPopover();
  // Always allow Escape to dismiss the account popover - including while
  // Connect is mid-approval - so a hung prompt doesn't trap the UI.
  else if (popoverOpen) closePopover();
});

function updateWalletDot(state) {
  if (!walletStatusDot) return;
  const phase = uiPhase === "connecting" ? uiPhase : computePhase(state);
  const participant = loadParticipant();
  const ready = isParticipantReady(participant);
  if (phase === "connected" && !ready) {
    walletStatusDot.dataset.state = "key-missing";
  } else {
    walletStatusDot.dataset.state = phase;
  }
  if (walletIconBtn) {
    walletIconBtn.title = ready
      ? "Account · ready"
      : state.authorized
        ? "Account · finish setup"
        : "Account";
  }
}

async function refreshBlockTip() {
  if (!statusPillHeight) return;
  try {
    const tip = await getBlockHeight();
    if (tip != null && Number.isFinite(Number(tip))) {
      statusPillHeight.textContent = Number(tip).toLocaleString();
      return;
    }
  } catch {
    /* keep previous */
  }
  if (!statusPillHeight.textContent || statusPillHeight.textContent === "-") {
    statusPillHeight.textContent = "-";
  }
}

function startBlockTipPoll() {
  refreshBlockTip();
  setInterval(refreshBlockTip, 10_000);
}

async function refreshCollateralChip() {
  if (!collateralChip) return;
  const key = loadTradingKey();
  if (!key?.commitment_hex) {
    paintCollateralChip(null);
    return;
  }
  try {
    const duskApp = await getChainApp();
    const bal = await duskApp.readContract({
      contract: "predictionMarket",
      functionName: "collateral_of",
      args: Array.from(hexToBytes(key.commitment_hex)),
    });
    paintCollateralChip(bal);
  } catch {
    paintCollateralChip(null);
  }
}

/** Allow other modules to push a known balance into the header chip. */
export function setCollateralChipLux(lux) {
  paintCollateralChip(lux);
}

/** Debug-only backend URL overrides (?debug=1). Not shown in normal UI. */
function renderDebugBackendSettings() {
  const wrap = document.createElement("div");
  wrap.className = "debug-backend";
  const settings = document.createElement("h3");
  settings.textContent = "Backend (debug)";
  wrap.append(settings);
  const cfg = loadBackendConfig();
  wrap.append(
    TextField({
      id: "gw-url",
      label: "Gateway URL",
      value: cfg.url,
      onInput: (v) => {
        saveBackendConfig({ ...loadBackendConfig(), url: v });
      },
    }),
    TextField({
      id: "gw-api-key",
      label: "Gateway API key",
      value: cfg.apiKey || "",
      onInput: (v) => {
        saveBackendConfig({ ...loadBackendConfig(), apiKey: v });
      },
    })
  );
  return wrap;
}

function go(hash) {
  closeAllPopovers();
  location.hash = hash;
}

function walletConnected() {
  const state = wallet.state;
  return Boolean(state?.authorized && state.profiles?.length);
}

function buildNavTabs(showCreate) {
  const tabs = [
    { id: "events", label: "Events" },
    { id: "portfolio", label: "Portfolio" },
    { id: "leaderboard", label: "Leaderboard" },
  ];
  if (walletConnected()) tabs.push({ id: "achievements", label: "Achievements" });
  if (showCreate) tabs.push({ id: "create", label: "Create" });
  tabs.push({ id: "about", label: "About" });
  return tabs;
}

function navTabId(routeName, showCreate) {
  if (routeName === "detail") return "events";
  if (routeName === "settings") return "";
  if (routeName === "create" && !showCreate) return "";
  if (routeName === "agent" || routeName === "agents") return "";
  if (routeName === "trader") return "";
  return routeName;
}

/** Create tab: contract owner/create-admin, or on-chain RIGHT_CREATE_MARKET. */
async function showCreateTab(privileged) {
  if (privileged) return true;
  const moonlightPk = connectedMoonlightAccount(wallet);
  if (!moonlightPk) return false;
  return walletHasCreateRight(dusk, moonlightPk);
}

function renderWalletCard(state) {
  const phase = uiPhase === "connecting" ? uiPhase : computePhase(state);
  updateWalletDot(state);

  const badgeByPhase = {
    "not-installed": ["danger", "No wallet detected"],
    picking: ["pending", "Choose a wallet"],
    connecting: ["pending", "Waiting for approval…"],
    connected: ["success", "Connected"],
    idle: ["neutral", "Not connected"],
  };
  const [variant, label] = badgeByPhase[phase] ?? ["neutral", phase];

  const stateRow = document.createElement("div");
  stateRow.className = "state-row";
  stateRow.append(Badge({ variant, label }));
  const participant = loadParticipant();
  const ready = isParticipantReady(participant);
  if (participant) {
    stateRow.append(
      Badge({
        variant: ready ? "success" : "pending",
        label: ready ? "Ready" : `Setup: ${participant.status}`,
      })
    );
  }

  const nodes = [stateRow];
  if (lastError) nodes.push(ErrorBanner({ message: lastError }));

  if (phase === "not-installed") {
    nodes.push(
      paragraph("No Dusk-compatible wallet provider was discovered."),
      ActionBar([
        Button({
          label: "Check again",
          variant: "primary",
          onClick: async () => {
            await wallet.refresh();
            log("re-checked for wallet providers");
          },
        }),
      ])
    );
  } else if (phase === "picking") {
    const picker = document.createElement("div");
    for (const provider of state.availableProviders) {
      picker.append(
        WalletOption({
          provider,
          onSelect: async (providerId) => {
            await wallet.selectProvider(providerId);
            log(`selected provider -> ${providerId}`);
          },
        })
      );
    }
    nodes.push(picker);
  } else if (phase === "connected") {
    if (state.providerId) {
      nodes.push(paragraph(`Provider: ${state.providerId}`));
    }
    for (const profile of state.profiles) {
      const account = profile?.account || profile?.address || "";
      const short =
        account.length > 20 ? `${account.slice(0, 10)}…${account.slice(-6)}` : account || "-";
      const row = document.createElement("div");
      row.className = "profile-row";
      const labelEl = document.createElement("span");
      labelEl.className = "label";
      labelEl.textContent = profile?.label || profile?.name || "Account";
      const valueWrap = document.createElement("span");
      valueWrap.className = "value wallet-popover-address";
      const addr = document.createElement("span");
      addr.className = "wallet-address-text";
      addr.textContent = short;
      addr.title = account;
      valueWrap.append(addr);
      if (account) {
        const copyBtn = document.createElement("button");
        copyBtn.type = "button";
        copyBtn.className = "copy-icon-btn";
        copyBtn.title = "Copy address";
        copyBtn.setAttribute("aria-label", "Copy address");
        copyBtn.append(icon("copy", { size: 14 }));
        copyBtn.addEventListener("click", async (e) => {
          e.stopPropagation();
          try {
            await navigator.clipboard.writeText(account);
            copyBtn.title = "Copied";
          } catch {
            copyBtn.title = "Copy failed";
          }
        });
        valueWrap.append(copyBtn);
      }
      row.append(labelEl, valueWrap);
      nodes.push(row);
    }
    if (popoverBalanceLux != null && ready) {
      const bal = document.createElement("p");
      bal.className = "popover-balance";
      bal.append(duskAmountNode(popoverBalanceLux), " free");
      nodes.push(bal);
    }
  } else {
    const discovering = !appReady;
    nodes.push(
      ActionBar([
        Button({
          label: discovering
            ? "Discovering wallets…"
            : phase === "connecting"
              ? "Connecting…"
              : "Connect wallet",
          variant: "primary",
          disabled: discovering || phase === "connecting",
          onClick: (e) => {
            e?.stopPropagation?.();
            if (!appReady || uiPhase === "connecting") return;
            lastError = null;
            uiPhase = "connecting";
            // Start the extension request in this click turn BEFORE any
            // re-render/await - browsers drop user-activation after yields,
            // and a concurrent wallet.ready() refresh can also stall the prompt.
            const providerName =
              wallet.state.providerInfo?.name || wallet.state.providerId || "wallet";
            log(`connect → ${providerName}`);
            const pending = Promise.race([
              wallet.connect(),
              new Promise((_, reject) => {
                setTimeout(() => {
                  reject(
                    new Error(
                      "Wallet did not respond (60s). Unlock the Dusk extension and try again, or use ?demoWallets=1 for a mock."
                    )
                  );
                }, 60_000);
              }),
            ]);
            renderWalletCard(wallet.state);
            void pending
              .then(() => {
                log("wallet connected");
              })
              .catch((err) => {
                lastError = err?.message || String(err);
                if (err?.code === ERROR_CODES?.USER_REJECTED) lastError = "Connection rejected";
                log(`connect failed: ${lastError}`);
              })
              .finally(() => {
                uiPhase = "idle";
                renderWalletCard(wallet.state);
              });
          },
        }),
      ])
    );
  }

  const nav = document.createElement("div");
  nav.className = "popover-nav";

  if (phase === "connected" && !ready) {
    nav.append(
      iconNavButton({
        label: "Finish setup",
        iconName: "settings",
        onClick: () => go("#/settings"),
      })
    );
  }
  if (ready) {
    nav.append(
      iconNavButton({
        label: "Funds",
        iconName: "deposit",
        onClick: () => openHeaderFunds("deposit"),
      }),
      iconNavButton({
        label: "Portfolio",
        iconName: "wallet",
        onClick: () => go("#/portfolio"),
      })
    );
  }
  nav.append(
    iconNavButton({
      label: "Account & settings",
      iconName: "settings",
      onClick: () => go("#/settings"),
    })
  );
  if (phase === "connected") {
    nav.append(
      iconNavButton({
        label: "Disconnect",
        iconName: "alert",
        variant: "is-danger",
        onClick: async () => {
          // Explicit disconnect: drop soft-onboard identity so the next visitor
          // on this browser cannot see the prior "You" / Ready state.
          clearIdentitySession();
          await wallet.disconnect();
          log("disconnected - cleared local participant and trading key");
          renderApp();
        },
      })
    );
  }
  if (!ready) {
    const hint = document.createElement("p");
    hint.className = "hint";
    hint.style.marginTop = "8px";
    hint.textContent = "Setup takes about two minutes.";
    nav.append(hint);
  }
  nodes.push(nav);

  if (debugMode) nodes.push(renderDebugBackendSettings());

  walletCardBody.replaceChildren(...nodes);
}

function renderAboutPage() {
  const root = document.createElement("div");
  root.className = "about-page";

  const hero = document.createElement("header");
  hero.className = "about-hero page-header";
  const badgeRow = document.createElement("div");
  badgeRow.className = "about-hero-meta";
  badgeRow.append(Badge({ variant: "neutral", label: "Testnet demo" }));
  const h1 = document.createElement("h1");
  h1.textContent = ABOUT_HERO.heading;
  const heroBody = document.createElement("p");
  heroBody.textContent = ABOUT_HERO.body;
  hero.append(badgeRow, h1, heroBody);
  root.append(hero);

  const tutorial = document.createElement("section");
  tutorial.className = "about-section dt-card";
  const tutorialH = document.createElement("h2");
  tutorialH.textContent = "How it works";
  const steps = document.createElement("ol");
  steps.className = "about-steps";
  for (const { step, body } of HOW_IT_WORKS) {
    const li = document.createElement("li");
    const strong = document.createElement("strong");
    strong.textContent = step;
    const p = document.createElement("p");
    p.textContent = body;
    li.append(strong, p);
    steps.append(li);
  }
  tutorial.append(tutorialH, steps);
  root.append(tutorial);

  const actionsCard = document.createElement("section");
  actionsCard.className = "about-section dt-card about-security-cta";
  const actionsH = document.createElement("h2");
  actionsH.textContent = "Keys, recovery & privacy";
  const actionsP = document.createElement("p");
  actionsP.textContent =
    "Moonlight pays gas and funds deposits; your trading key signs trades. Backups and disconnect behavior are in Settings. How we process data is in the privacy policy.";
  actionsCard.append(
    actionsH,
    actionsP,
    ActionBar([
      Button({
        label: ABOUT_SECURITY_CTA.label,
        variant: "primary",
        onClick: () => {
          location.hash = ABOUT_SECURITY_CTA.hash;
        },
      }),
      privacyPolicyLink(),
    ])
  );
  root.append(actionsCard);

  for (const section of ABOUT_SECTIONS) {
    const block = document.createElement("section");
    block.className = "about-section dt-card";
    const h = document.createElement("h2");
    h.textContent = section.title;
    const p = document.createElement("p");
    p.textContent = section.body;
    block.append(h, p);
    root.append(block);
  }

  const legal = document.createElement("section");
  legal.className = "about-legal dt-card";
  const legalH = document.createElement("h2");
  legalH.textContent = "Testnet & legal basics";
  legal.append(legalH);
  for (const section of LEGAL_SECTIONS) {
    const block = document.createElement("div");
    block.className = "about-legal-item";
    const h = document.createElement("h3");
    h.textContent = section.title;
    const p = document.createElement("p");
    p.textContent = section.body;
    block.append(h, p);
    if (section.href && section.linkLabel) {
      const a = document.createElement("a");
      a.href = section.href;
      a.textContent = section.linkLabel;
      block.append(a);
    }
    legal.append(block);
  }
  root.append(legal);

  return root;
}

// Pages that poll (browse-page.js, market-detail.js - see data-cache.js)
// register a cleanup here; every renderApp() call tears down whatever the
// previous page started before rendering the next one, so a poll interval
// never keeps running against a route the user has navigated away from.
let stopCurrentPagePolling = null;
/** Bumps on every renderApp entry so a superseded async mount doesn't
 *  clobber a newer route (stuck "Loading…" / stale browse after wallet blips). */
let renderGeneration = 0;

async function renderApp() {
  const gen = ++renderGeneration;
  stopCurrentPagePolling?.();
  stopCurrentPagePolling = null;
  const owner = isContractOwner(wallet);
  const privileged = isPrivilegedCreator(wallet);
  const showCreate = await showCreateTab(privileged);
  if (gen !== renderGeneration) return;
  lastCreateTabFlag = showCreate;
  setFeedbackVisible(owner);
  const tabId = navTabId(route.name, showCreate);

  appTabs.replaceChildren(
    Tabs({
      tabs: buildNavTabs(showCreate),
      activeId: tabId,
      onChange: (id) => {
        location.hash = `#/${id}`;
      },
    })
  );

  appBody.replaceChildren(paragraph("Loading…"));
  try {
    let content;
    if (route.name === "about") {
      content = renderAboutPage();
    } else if (route.name === "invalid-route") {
      content = ErrorBanner({
        message: "Invalid commitment in URL - use 64 lowercase hex digits.",
      });
    } else if (route.name === "create") {
      content = await renderCreatePage({
        wallet,
        log,
        onCreated: (id) => {
          location.hash = `#/events/${id}`;
        },
      });
    } else if (route.name === "settings") {
      content = renderSettingsPage({
        wallet,
        log,
        section: route.section,
        onChange: () => {
          refreshCollateralChip();
          renderWalletCard(wallet.state);
        },
      });
    } else if (route.name === "portfolio") {
      content = await renderPortfolioPage({
        log,
        onOpenMarket: (id) => {
          location.hash = `#/events/${id}`;
        },
        onRefresh: () => renderApp(),
        onOpenSettings: () => {
          location.hash = "#/settings";
        },
        onCollateralChange: setCollateralChipLux,
      });
    } else if (route.name === "leaderboard") {
      content = await renderLeaderboardPage({ log, wallet });
    } else if (route.name === "agents") {
      content = renderAgentsPage({ log });
    } else if (route.name === "trader") {
      content = await renderTraderPage({
        commitmentHex: route.commitmentHex,
        log,
        registerCleanup: (fn) => {
          if (gen === renderGeneration) stopCurrentPagePolling = fn;
          else fn?.();
        },
      });
    } else if (route.name === "agent") {
      content = await renderAgentPage({
        commitmentHex: route.commitmentHex,
        log,
        onOpenMarket: (id) => {
          location.hash = `#/events/${id}`;
        },
      });
    } else if (route.name === "achievements") {
      content = await renderAchievementsPage({
        wallet,
        log,
        onOpenSettings: () => {
          location.hash = "#/settings";
        },
      });
    } else if (route.name === "detail") {
      content = await renderMarketDetail({
        marketId: route.id,
        log,
        onBack: () => {
          location.hash = "#/events";
        },
        onRefresh: () => renderApp(),
        onCollateralChange: setCollateralChipLux,
        registerCleanup: (fn) => {
          if (gen === renderGeneration) stopCurrentPagePolling = fn;
          else fn?.();
        },
      });
    } else {
      content = await renderBrowsePage({
        log,
        onOpen: (id) => {
          location.hash = `#/events/${id}`;
        },
        registerCleanup: (fn) => {
          if (gen === renderGeneration) stopCurrentPagePolling = fn;
          else fn?.();
        },
      });
    }
    if (gen !== renderGeneration) return;
    appBody.replaceChildren(content);
    if (route.name === "settings" || route.name === "portfolio") {
      refreshCollateralChip();
    }
  } catch (e) {
    if (gen !== renderGeneration) return;
    appBody.replaceChildren(ErrorBanner({ message: e.message || String(e) }));
  }
}

let lastOwnerFlag = null;
let lastPrivilegedFlag = null;
let lastCreateTabFlag = null;
let lastMoonlightAccount = "";
let appReady = false;
// Wallet extensions report "locked" and "disconnected" identically (no
// separate locked state in the connect protocol - see errors.ts), so an
// idle-timeout auto-lock fires the same event a real disconnect would.
// Reacting to that instantly by tearing down/remounting the current page
// (renderApp() below replaces appBody wholesale) is what makes a
// transient blip feel like getting kicked out mid-interaction - worst on
// the Create page, where it silently orphans an in-flight submit()'s DOM
// references (tx still completes, but its feedback writes to a detached
// node the user can no longer see). Give a losing-owner transition a
// short grace window and a non-prompting wallet.refresh() (re-queries
// already-approved profiles without popping the extension UI) before
// treating it as real; a true disconnect still remounts, just not on the
// first blip.
let lostOwnerGraceTimer = null;

/** Rebuild primary nav + feedback chrome without tearing down appBody. */
async function refreshOwnerChrome() {
  const owner = isContractOwner(wallet);
  const privileged = isPrivilegedCreator(wallet);
  const showCreate = await showCreateTab(privileged);
  lastCreateTabFlag = showCreate;
  setFeedbackVisible(owner);
  const tabId = navTabId(route.name, showCreate);
  appTabs.replaceChildren(
    Tabs({
      tabs: buildNavTabs(showCreate),
      activeId: tabId,
      onChange: (id) => {
        location.hash = `#/${id}`;
      },
    })
  );
}

/**
 * Privileged-creator flips normally remount the page (Create tab appears/disappears).
 * Skip the remount while the Create form is up and the wallet is still privileged -
 * a Connect blip must not orphan an in-flight create submit.
 * Losing privileged status still remounts so the operator-only empty state shows.
 */
function remountForPrivilegedChange(privileged) {
  lastPrivilegedFlag = privileged;
  if (route.name === "create" && (privileged || lastCreateTabFlag)) {
    void refreshOwnerChrome();
    return;
  }
  renderApp();
}

wallet.subscribe(() => {
  renderWalletCard(wallet.state);
  refreshCollateralChip();
  // Don't renderApp until dusk.ready() - early calls hang browse on getChainApp().
  if (!appReady) return;
  const owner = isContractOwner(wallet);
  const privileged = isPrivilegedCreator(wallet);
  const account = connectedMoonlightAccount(wallet);

  if (account !== lastMoonlightAccount) {
    // Account switch (A→B): clear cached identity so B does not inherit A's
    // participant / trading key / Leaderboard "You". Empty account is often a
    // lock blip (same as disconnect in the Connect protocol) - keep cache so
    // unlock does not force re-import; "You" still requires an authorized wallet.
    if (lastMoonlightAccount && account) {
      clearIdentitySession();
      log(
        `moonlight account changed - cleared local participant and trading key`
      );
    }
    lastMoonlightAccount = account;
    lastOwnerFlag = owner;
    lastPrivilegedFlag = privileged;
    setFeedbackVisible(owner);
    renderApp();
    return;
  }

  if (owner === lastOwnerFlag && privileged === lastPrivilegedFlag) return;

  if (owner !== lastOwnerFlag) {
    lastOwnerFlag = owner;
    setFeedbackVisible(owner);
    void refreshOwnerChrome();
  }

  if (privileged === lastPrivilegedFlag) return;

  if (privileged) {
    if (lostOwnerGraceTimer) {
      clearTimeout(lostOwnerGraceTimer);
      lostOwnerGraceTimer = null;
    }
    remountForPrivilegedChange(true);
    return;
  }

  if (lostOwnerGraceTimer) return; // already grace-checking a prior blip
  lostOwnerGraceTimer = setTimeout(async () => {
    lostOwnerGraceTimer = null;
    try {
      await wallet.refresh();
    } catch (e) {
      log(`wallet refresh during grace check failed: ${e?.message || e}`);
    }
    const stillLost = !isPrivilegedCreator(wallet);
    if (stillLost) {
      if (lastPrivilegedFlag !== false) {
        remountForPrivilegedChange(false);
      }
    } else {
      lastPrivilegedFlag = true;
    }
  }, 1500);
});

dusk.ready().then(() => {
  appReady = true;
  lastOwnerFlag = isContractOwner(wallet);
  lastPrivilegedFlag = isPrivilegedCreator(wallet);
  lastMoonlightAccount = connectedMoonlightAccount(wallet);
  setFeedbackVisible(lastOwnerFlag);
  log("chain app ready");
  renderWalletCard(wallet.state);
  refreshCollateralChip();
  startBlockTipPoll();
  renderApp();
});
